import { useSelector, useDispatch } from "react-redux";
import { useEffect , useState, useRef } from "react";
import { Container, Grid, Box, Typography } from "@mui/material";

import Input from "@/components/ph/Input";
import Select from "@/components/ph/Select";

import FormSection from "../../utils/FormSection";

import { generateDays, generateMonths, generateYears } from "../hooks/DateHelpers"

import { setFormValue, setValidValue } from "../contractorSlice";
import { getTaxRegime } from "../contractorSlice";

import { 
    setFormValue as setFormValueDriver, 
    setValidValue as setValidValueDriver 
} from "../../driver/driverSlice";

import { useContractorValidation } from "../hooks/useContractorValidation"
import { useDateField } from "../hooks/useDateField";

const ContractorPersonalData = () => {
    const dispatch = useDispatch();
    const formValues = useSelector((state) => state.contractor.formValues);
    const driverFormValues = useSelector((state) => state.driver.formValues);
    const regimenFiscalOptions = useSelector((state) => state.contractor.regimenFiscalOptions);

    const [disabledFields, setDisabledFields] = useState(false);

    useEffect(() => {
        dispatch(getTaxRegime());
    }, [dispatch]);

    const parseDateFromString = (dateString) => {
        if (!dateString || dateString.length !== 8) return { day: '', month: '', year: '' };

        try {
            const year = dateString.substring(0, 4);
            const month = String(Number(dateString.substring(4, 6)));
            const day = String(Number(dateString.substring(6, 8)));
            return { day, month, year };
        } catch (error) {
            return { day: '', month: '', year: '' };
        }
    };

    const currentDay = formValues.dia || parseDateFromString(formValues.fechaNacimiento).day;
    const currentMonth = formValues.mes || parseDateFromString(formValues.fechaNacimiento).month;
    const currentYear = formValues.anio || parseDateFromString(formValues.fechaNacimiento).year;

    useEffect(() => {
        setDisabledFields(formValues.esConductorHabitual === "Si");
    }, [formValues.esConductorHabitual]);

    useEffect(() => {
        if (formValues.esConductorHabitual === "Si") {
            const fields = ['primerApellido', 'segundoApellido'];
            fields.forEach((field) => {
                const value = formValues[field] || '';
                if (driverFormValues[field] !== value) {
                    dispatch(setFormValueDriver({ field, value }));
                    dispatch(setValidValueDriver({ field, isValid: !!value }));
                }
            });
        }
    }, [formValues, driverFormValues, dispatch]);

    const handleDateChange = (field, value) => {
        const currentDay = field === "dia" ? value : formValues.dia || "";
        const currentMonth = field === "mes" ? value : formValues.mes || "";
        const currentYear = field === "anio" ? value : formValues.anio || "";

        dispatch(setFormValue({ field, value }));

        if (currentDay && currentMonth && currentYear) {
            const formattedDate = `${currentYear}${currentMonth.padStart(
                2,
                "0"
            )}${currentDay.padStart(2, "0")}`;
            dispatch(
                setFormValue({ field: "fechaNacimiento", value: formattedDate })
            );
        }
    };
    
    const handleDayChange = (e) => handleDateChange("dia", e.target.value);
    const handleMonthChange = (e) => handleDateChange("mes", e.target.value);
    const handleYearChange = (e) => handleDateChange("anio", e.target.value);

    const nombresField = useContractorValidation("nombres", {
        required: true,
        validate: (v) => {
            if (!/^[A-Za-zÁÉÍÓÚÑáéíóúñ\s'-]+$/.test(v))
                return "El nombre solo puede contener letras";
            if (v.length < 3)
                return "El nombre debe tener al menos 3 caracteres";
            return "";
        },
    });

    const primerApellidoField = useContractorValidation("primerApellido", {
        required: true,
        validate: (v) => {
            if (!/^[A-Za-zÁÉÍÓÚÑáéíóúñ\s'-]+$/.test(v))
                return "El apellido paterno solo puede contener letras";
            if (v.length < 3)
                return "El apellido paterno debe tener al menos 3 caracteres";
            return "";
        },
    });

    const segundoApellidoField = useContractorValidation("segundoApellido", {
        required: true,
        validate: (v) => {
            if (!/^[A-Za-zÁÉÍÓÚÑáéíóúñ\s'-]+$/.test(v))
                return "El apellido materno solo puede contener letras";
            if (v.length < 3)
                return "El apellido materno debe tener al menos 3 caracteres";
            return "";
        },
    });

    const dateField = useDateField();

    const generoField = useContractorValidation("genero", { required: true });
    const regimenField = useContractorValidation("regimenFiscal", {
        required: false,
    });

    const rfcField = useContractorValidation("rfc", { 
        required: true,
        validate: (val) => {
            if (!val || val.trim() === "") return "";
            
            const cleanRFC = val.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
            
            if (cleanRFC.length !== 13) {
                return "El RFC debe tener 13 caracteres";
            }
            
            const rfcRegex = /^[A-Z&Ñ]{3,4}[0-9]{6}[A-Z0-9]{3}$/;
            if (!rfcRegex.test(cleanRFC)) {
                return "Formato de RFC no válido";
            }
            
            return "";
        },
        transformValue: (v) => {
            return v.replace(/[^a-zA-Z0-9]/g, "").toUpperCase().slice(0, 13);
        },
        onKeyDown: (e) => {
            const allowedKeys = [
                'Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 
                'Home', 'End', 'Enter'
            ];
            
            if (allowedKeys.includes(e.key)) return;
            if (!/^[a-zA-Z0-9&]$/.test(e.key)) {
                e.preventDefault();
            }
            if (e.target.value.length >= 13 && !allowedKeys.includes(e.key)) {
                e.preventDefault();
            }
        },
    });

    return (
        <Container maxWidth="md" disableGutters={{ xs: true, md: false }}>
            <FormSection>
                <Grid container spacing={2} size={{ xs: 12, md: 12 }}>
                    <Grid size={{ xs: 12, md: 12 }}>
                        <Typography
                            variant="body1"
                            sx={{
                                color: "text.secondary",
                                mt: 1,
                                mb: 1,
                                fontSize: "1rem",
                                fontWeight: "500",
                            }}
                        >
                            DATOS PERSONALES DEL CONTRATANTE
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 12 }}>
                        <Box
                            sx={{
                                minHeight: "3.5rem",
                                display: "flex",
                                alignItems: "flex-end",
                            }}
                        >
                            <Input
                                errorMessage={nombresField.getErrorMessage()}
                                isInvalid={nombresField.isInvalid}
                                label="Nombres"
                                name="nombres"
                                value={nombresField.value}
                                onChange={nombresField.onChange}
                                onBlur={nombresField.onBlur}
                                required
                                type="text"
                            />
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Box
                            sx={{
                                minHeight: "3.5rem",
                                display: "flex",
                                alignItems: "flex-end",
                            }}
                        >
                            <Input
                                errorMessage={primerApellidoField.getErrorMessage()}
                                isInvalid={primerApellidoField.isInvalid}
                                label="Primer Apellido"
                                name="primerApellido"
                                value={primerApellidoField.value}
                                onChange={primerApellidoField.onChange}
                                onBlur={primerApellidoField.onBlur}
                                required
                                type="text"
                            />
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Box
                            sx={{
                                minHeight: "3.5rem",
                                display: "flex",
                                alignItems: "flex-end",
                            }}
                        >
                            <Input
                                errorMessage={segundoApellidoField.getErrorMessage()}    
                                isInvalid={segundoApellidoField.isInvalid}                        
                                label="Segundo Apellido"
                                name="segundoApellido"
                                value={segundoApellidoField.value}
                                onChange={segundoApellidoField.onChange}
                                onBlur={segundoApellidoField.onBlur}
                                required
                                type="text"
                            />
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <Typography variant="body1" sx={{ color: "text.secondary", fontSize: ".8rem" }}>
                            Fecha de Nacimiento
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 12 }}>
                        <Grid container spacing={2} alignItems="flex-end">
                            <Grid size={{ xs: 12, md: 12 }}>
                                <Grid container spacing={1} className="fecha-selects">
                                    <Grid size={{ xs: 3 }}>
                                        <Box
                                            sx={{
                                                minHeight: "3.5rem",
                                                display: "flex",
                                                alignItems: "flex-end",
                                            }}
                                        >
                                            <Select
                                                type="labeled"
                                                label="Día"
                                                name="dia"
                                                value={currentDay}
                                                isInvalid={dateField.isDateFieldInvalid("dia")}
                                                options={generateDays()}
                                                disabled={disabledFields}
                                                onChange={handleDayChange}
                                                required
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid size={{ xs: 5 }}>
                                        <Box
                                            sx={{
                                                minHeight: "3.5rem",
                                                display: "flex",
                                                alignItems: "flex-end",
                                            }}
                                        >
                                            <Select
                                                type="labeled"
                                                label="Mes"
                                                name="mes"
                                                value={currentMonth}
                                                isInvalid={dateField.isDateFieldInvalid("mes")}
                                                options={generateMonths()}
                                                disabled={disabledFields}
                                                onChange={handleMonthChange}
                                                required
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid size={{ xs: 4 }}>
                                        <Box
                                            sx={{
                                                minHeight: "3.5rem",
                                                display: "flex",
                                                alignItems: "flex-end",
                                            }}
                                        >
                                            <Select
                                                type="labeled"
                                                label="Año"
                                                name="anio"
                                                value={currentYear}
                                                isInvalid={dateField.isDateFieldInvalid("anio")}
                                                options={generateYears()}
                                                disabled={disabledFields}
                                                onChange={handleYearChange}
                                                required
                                            />
                                        </Box>
                                    </Grid>
                                </Grid>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: "error.main",
                                        display: "block",
                                        mt: "-1.6rem",
                                        fontSize: "0.8rem",
                                        fontWeight: 500,
                                        height: dateField.dateError ? "auto" : "1rem",
                                    }}
                                >
                                    {dateField.dateError || " "}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid size={{ xs: 12, md: 12 }}>
                        <Grid container spacing={2} alignItems="flex-end">
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Box
                                    sx={{
                                        minHeight: "3.5rem",
                                        display: "flex",
                                        alignItems: "flex-end",
                                    }}
                                >
                                    <Input
                                        errorMessage={rfcField.getErrorMessage()}
                                        isInvalid={rfcField.isInvalid}
                                        label="RFC + homoclave"
                                        name="rfc"
                                        value={rfcField.value}
                                        onChange={rfcField.onChange}
                                        onBlur={rfcField.onBlur}
                                        {...rfcField.handlers}
                                        type="text"
                                        required
                                    />
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Box
                                    sx={{
                                        minHeight: "3.5rem",
                                        display: "flex",
                                        alignItems: "flex-end",
                                    }}
                                >
                                    <Select
                                        errorMessage={generoField.getErrorMessage()}
                                        isInvalid={generoField.isInvalid}
                                        label="Género"
                                        name="genero"
                                        value={generoField.value}
                                        onChange={generoField.onChange}
                                        onBlur={generoField.onBlur}
                                        options={[
                                            { value: "M", label: "Masculino" },
                                            { value: "F", label: "Femenino" },
                                        ]}
                                        required
                                        disabled={disabledFields}
                                        type="labeled"
                                    />
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12, md: 12 }}>
                                <Box
                                    sx={{
                                        minHeight: "3.5rem",
                                        display: "flex",
                                        alignItems: "flex-end",
                                    }}
                                >
                                    <Select
                                        errorMessage={regimenField.getErrorMessage()}
                                        isInvalid={regimenField.isInvalid}
                                        type="labeled"
                                        label="Regimen fiscal (Opcional)"
                                        name="regimenFiscal"
                                        value={regimenField.value}
                                        onChange={regimenField.onChange}
                                        onBlur={regimenField.onBlur} 
                                        options={regimenFiscalOptions}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    </Grid>                  
                </Grid>
            </FormSection>
        </Container>
    );
};

export default ContractorPersonalData;